import { useState, useEffect, useCallback, useRef } from "react";
import { useScrollReveal, getRevealClasses } from "@/hooks/useScrollReveal";
import { allExperts, type ExpertProfile } from "@/mocks/homepageData";

const DISPLAY_COUNT = 3;
const ROTATE_INTERVAL_MS = 6000;

function pickRandomExperts(
  pool: ExpertProfile[],
  count: number,
  exclude: ExpertProfile[]
): ExpertProfile[] {
  const available = pool.filter((e) => !exclude.some((x) => x.name === e.name));
  const shuffled = [...available].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, count);
}

function getInitialExperts(): ExpertProfile[] {
  const shuffled = [...allExperts].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, DISPLAY_COUNT);
}

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <i
          key={i}
          className={`text-xs ${
            i < rating ? "ri-star-fill text-[#F59E0B]" : "ri-star-line text-[#374151]"
          }`}
        />
      ))}
    </div>
  );
}

function ExpertCard({
  expert,
  delay,
  isLeaving,
}: {
  expert: ExpertProfile;
  delay: number;
  isLeaving: boolean;
}) {
  const { ref, isVisible } = useScrollReveal(0.1);

  return (
    <div
      ref={ref}
      className={`group relative bg-[#1F2937] rounded-xl border border-[#374151] p-6 md:p-8 transition-all duration-500 hover:border-[#7C3AED]/40 ${
        isLeaving ? "opacity-0 translate-y-4 scale-95" : ""
      } ${
        isVisible
          ? "opacity-100 translate-y-0 scale-100"
          : "opacity-0 translate-y-8 scale-95"
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {/* Subtle gradient glow on hover */}
      <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-[#7C3AED]/5 via-transparent to-[#10B981]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />

      {/* Top badge row */}
      <div className="relative flex items-center justify-between mb-5">
        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#10B981]/10 border border-[#10B981]/25">
          <i className="ri-verified-badge-fill text-[#10B981] text-xs" />
          <span className="text-[#10B981] text-xs font-medium">{expert.badge}</span>
        </div>
        <StarRating rating={expert.rating} />
      </div>

      <div className="relative">
        {/* Quote icon */}
        <div className="mb-5">
          <div className="w-10 h-10 rounded-lg bg-[#7C3AED]/15 flex items-center justify-center">
            <i className="ri-double-quotes-l text-[#7C3AED] text-lg" />
          </div>
        </div>

        {/* Quote text */}
        <p className="text-[#94A3B8] text-sm leading-relaxed mb-8 italic">
          "{expert.quote}"
        </p>

        {/* Author */}
        <div className="flex items-center gap-3 md:gap-4 min-w-0">
          <div className="w-11 h-11 md:w-14 md:h-14 rounded-full overflow-hidden flex-shrink-0 ring-2 ring-[#374151] group-hover:ring-[#7C3AED]/40 transition-all duration-500">
            <img
              src={expert.avatar}
              alt={expert.name}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="min-w-0 flex-1 overflow-hidden">
            <h4 className="text-[#F8FAFC] font-semibold text-sm md:text-base truncate">
              {expert.name}
            </h4>
            <p className="text-[#64748B] text-xs md:text-sm leading-snug line-clamp-2">
              {expert.title}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ExpertVerdictSection() {
  const { ref: headerRef, isVisible: headerVisible } = useScrollReveal(0.1);
  const [displayedExperts, setDisplayedExperts] = useState<ExpertProfile[]>(getInitialExperts);
  const [isLeaving, setIsLeaving] = useState(false);
  const [direction, setDirection] = useState<"right" | "left">("right");
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const sectionRef = useRef<HTMLElement | null>(null);

  const rotateExperts = useCallback(() => {
    setIsLeaving(true);
    setDirection((prev) => (prev === "right" ? "left" : "right"));

    setTimeout(() => {
      setDisplayedExperts((prev) => {
        const next = pickRandomExperts(allExperts, DISPLAY_COUNT, prev);
        // If pickRandomExperts returned less than DISPLAY_COUNT (shouldn't happen with 10),
        // fallback to random selection from full pool
        if (next.length < DISPLAY_COUNT) {
          const fallback = [...allExperts].sort(() => Math.random() - 0.5);
          return fallback.slice(0, DISPLAY_COUNT);
        }
        return next;
      });
      setIsLeaving(false);
    }, 400);
  }, []);

  // Rotate on interval, but only when section is visible
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          intervalRef.current = setInterval(rotateExperts, ROTATE_INTERVAL_MS);
        } else {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
        }
      },
      { threshold: 0.15 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      observer.disconnect();
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [rotateExperts]);

  const lastUpdated = new Date().toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });

  return (
    <section ref={sectionRef} className="py-20 md:py-28 bg-[#111827]">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* Section Header */}
        <div
          ref={headerRef}
          className={`text-center mb-12 md:mb-16 transition-all duration-700 ${getRevealClasses(
            headerVisible,
            "up"
          )}`}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#7C3AED]/40 mb-6">
            <i className="ri-verified-badge-line text-[#7C3AED]" />
            <span className="text-sm text-[#F8FAFC]">Expert Verdict</span>
            {/* Live indicator */}
            <div className="flex items-center gap-1.5 ml-2 pl-2 border-l border-[#374151]">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#10B981] opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#10B981]"></span>
              </span>
              <span className="text-xs text-[#94A3B8]">Live Opinions</span>
            </div>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#F8FAFC] mb-4">
            Trusted by{" "}
            <span className="text-gradient">Industry Experts</span>
          </h2>
          <p className="text-[#94A3B8] text-lg max-w-2xl mx-auto mb-3">
            Hear what professional traders, quants, and educators say about our
            transparent broker analysis.
          </p>
          <div className="flex items-center justify-center gap-2 text-xs text-[#64748B]">
            <i className="ri-refresh-line" />
            <span>Opinions rotate automatically. Last shuffle: {lastUpdated}</span>
          </div>
        </div>

        {/* Expert Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-8">
          {displayedExperts.map((expert, index) => (
            <ExpertCard
              key={expert.name}
              expert={expert}
              delay={isLeaving ? 0 : index * 150}
              isLeaving={isLeaving}
            />
          ))}
        </div>

        {/* Shuffle hint */}
        <div className="flex items-center justify-center gap-3 mt-10">
          <button
            onClick={rotateExperts}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1F2937] border border-[#374151] text-sm text-[#94A3B8] hover:border-[#7C3AED]/40 hover:text-[#F8FAFC] transition-all duration-300 cursor-pointer"
          >
            <i className="ri-shuffle-line text-[#7C3AED]" />
            <span>Shuffle More Opinions</span>
          </button>
        </div>
      </div>
    </section>
  );
}